import React, { useEffect, useRef } from "react";
import featureImg from "../../assets/image/Feature.png";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&display=swap');

  .abha-banner {
    position: relative;
    min-height: 100vh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    /* Dark navy space background — matches screenshot */
    background: radial-gradient(ellipse at 20% 40%, #0a1628 0%, #060d1a 50%, #000 100%);
  }

  /* Particle canvas */
  .abha-banner__canvas {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
    display: block;
  }

  /* Feature.png — curved blue glow, centered vertically */
  .abha-banner__feature {
    position: absolute;
    bottom: 120px;
    left: 0;
    width: 100%;
    z-index: 2;
   
    line-height: 0;
  }
  .abha-banner__feature img {
    width: 100%;
    height: auto;
    display: block;
  }

  /* Content */
  .abha-banner__content {
    position: relative;
    z-index: 10;
    flex: 1;
    display: flex;
    align-items: center;
    padding-top: 100px;
    padding-bottom: 200px;
    width: 100%;
    justify-content: center;
  }

  .abha-banner__title {
    font-family: 'Manrope', sans-serif;
    font-size: clamp(2rem, 5.5vw, 3.8rem);
    font-weight: 800;
    line-height: 1.18;
    color: #fff;
    letter-spacing: -0.5px;
    margin-bottom: 20px;
    animation: abha-fadeUp 0.9s ease both;
  }

  .abha-banner__desc {
    font-family: 'Manrope', sans-serif;
    font-size: clamp(0.82rem, 1.6vw, 0.97rem);
    color: #FFFFFF;
    line-height: 1.75;
    width: 80%;
    margin: 0 auto 40px;
    animation: abha-fadeUp 0.9s 0.15s ease both;
  }

  .abha-banner__btn {
    font-family: 'Manrope', sans-serif;
    display: inline-block;
    background: #1a7fff;
    color: #fff;
    font-size: 0.97rem;
    font-weight: 600;
    padding: 15px 46px;
    border-radius: 999px;
    border: none;
    cursor: pointer;
    letter-spacing: 0.2px;
    transition: background 0.25s, box-shadow 0.25s, transform 0.2s;
    box-shadow: 0 0 24px rgba(26,127,255,0.55), 0 0 50px rgba(26,127,255,0.18);
    animation: abha-fadeUp 0.9s 0.28s ease both, abha-glowPulse 2.8s 1.2s ease-in-out infinite;
  }
  .abha-banner__btn:hover {
    background: #3391ff;
    box-shadow: 0 0 40px rgba(51,145,255,0.75), 0 0 80px rgba(51,145,255,0.28);
    transform: translateY(-2px);
  }
  .abha-banner__btn:active { transform: translateY(0); }

  /* Info Bar */
  .abha-info-bar {
    background: linear-gradient(90deg, #04123a 0%, #0d5bd4 25%, #1a7fff 50%, #0d5bd4 75%, #04123a 100%);
    padding: 18px 0;
    position: relative;
    z-index: 20;
  }
  .abha-info-bar__item {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 12px 28px;
  }
  .abha-info-bar__item--bordered {
    border-left: 1px solid rgba(255,255,255,0.22);
  }
  .abha-info-bar__icon {
    flex-shrink: 0;
    width: 46px;
    height: 46px;
    border-radius: 12px;
    background: rgba(255,255,255,0.12);
    border: 1px solid rgba(255,255,255,0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.15rem;
    color: #fff;
  }
  .abha-info-bar__text {
    font-family: 'Manrope', sans-serif;
    font-size: 0.82rem;
    font-weight: 500;
    color: #fff;
    line-height: 1.5;
  }

  /* Keyframes */
  @keyframes abha-fadeUp {
    from { opacity: 0; transform: translateY(22px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes abha-glowPulse {
    0%,100% { box-shadow: 0 0 24px rgba(26,127,255,0.55), 0 0 50px rgba(26,127,255,0.18); }
    50%      { box-shadow: 0 0 48px rgba(26,127,255,0.88), 0 0 100px rgba(26,127,255,0.32); }
  }

  /* Responsive */
  @media (max-width: 991px) {
    .abha-info-bar__item--bordered { border-left: none; border-top: 1px solid rgba(255,255,255,0.18); }
    .abha-info-bar__item { padding: 12px 20px; }
  }
  @media (max-width: 767px) {
    .abha-banner__content { padding-top: 90px; padding-bottom: 160px; }
    .abha-info-bar__item--bordered { border-left: none; border-top: 1px solid rgba(255,255,255,0.18); }
  }
  @media (max-width: 480px) {
    .abha-banner__btn { padding: 13px 34px; font-size: 0.9rem; }
  }
`;

const Banner = ({ formRef }) => {
  const canvasRef    = useRef(null);
  const mouseRef     = useRef({ x: 0, y: 0 });
  const featureRef   = useRef(null);
  const bgRef        = useRef(null);

  /* ── Inject styles ── */
  useEffect(() => {
    if (document.getElementById("abha-banner-styles")) return;
    const tag = document.createElement("style");
    tag.id = "abha-banner-styles";
    tag.innerHTML = styles;
    document.head.appendChild(tag);
    return () => tag.remove();
  }, []);

  /* ── Mouse parallax ── */
  useEffect(() => {
    const onMove = (e) => {
      const cx = window.innerWidth  / 2;
      const cy = window.innerHeight / 2;
      mouseRef.current = {
        x: (e.clientX - cx) / cx,   // -1 to 1
        y: (e.clientY - cy) / cy,
      };
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  /* ── Particle canvas ── */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animId;
    let particles = [];
    /* parallax smoothing */
    let smoothX = 0, smoothY = 0;

    const rand = (min, max) => Math.random() * (max - min) + min;

    const resize = () => {
      canvas.width  = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };

    const makeParticle = () => ({
      x:     rand(0, canvas.width),
      y:     rand(0, canvas.height),
      r:     rand(0.4, 1.6),          
      vx:    rand(-0.14, 0.14),
      vy:    rand(-0.10, 0.10),
      alpha: rand(0.18, 0.45),        
    });

    const init = () => {
      resize();
      particles = Array.from({ length: 110 }, makeParticle);
    };

    const draw = () => {
      const { width: W, height: H } = canvas;

      /* Smooth mouse */
      smoothX += (mouseRef.current.x - smoothX) * 0.04;
      smoothY += (mouseRef.current.y - smoothY) * 0.04;

      /* Parallax on feature image */
      if (featureRef.current) {
        featureRef.current.style.transform =
          `translate(${smoothX * -18}px, ${smoothY * -10}px)`;
      }

      ctx.clearRect(0, 0, W, H);

      const LINK = 130;

      /* Connection lines — clearly visible */
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const d  = Math.sqrt(dx * dx + dy * dy);
          if (d < LINK) {
            const opacity = 0.38 * (1 - d / LINK);   // more visible lines
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(70,150,255,${opacity})`;
            ctx.lineWidth   = 0.85;
            ctx.stroke();
          }
        }
      }

      /* Dots — smaller & dimmer */
      particles.forEach((p) => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(140,195,255,${p.alpha})`;
        ctx.fill();

        /* Parallax offset on particles */
        p.x += p.vx + smoothX * 0.08;
        p.y += p.vy + smoothY * 0.05;

        if (p.x < -10)    p.x = W + 10;
        if (p.x > W + 10) p.x = -10;
        if (p.y < -10)    p.y = H + 10;
        if (p.y > H + 10) p.y = -10;
      });

      animId = requestAnimationFrame(draw);
    };

    init();
    draw();
    window.addEventListener("resize", resize);
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
    };
  }, []);

  const handleClick = () => {
    formRef?.current?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      {/* ── BANNER ── */}
      <section className="abha-banner" ref={bgRef}>

        <canvas ref={canvasRef} className="abha-banner__canvas" />

        {/* Content */}
        <div className="container abha-banner__content">
          <div className="row justify-content-center w-100">
            <div className="col-12 col-md-10 col-lg-7 text-center">
              <h1 className="abha-banner__title">
                Create Ayushman Bharat<br />Health Account
              </h1>
              <p className="abha-banner__desc">
                ABHA Number Card is a 14-digit unique identification number
                that serves as an identifier for individuals in India's
                digital healthcare ecosystem.
              </p>
              <button
                className="abha-banner__btn"
                id="createabha"
                onClick={handleClick}
              >
                Create ABHA Address
              </button>
            </div>
          </div>
        </div>

        {/* Feature image — curved blue glow with parallax */}
        <div className="abha-banner__feature" ref={featureRef}>
          <img src={featureImg} alt="" aria-hidden="true" />
        </div>

      </section>

    
    </>
  );
};

export default Banner;